import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Button, Card, KpiCard, RuledPage } from '@kollez/design-system';
import { useAuthStore } from '@/lib/authStore';
import { useOrgTree, type OrgTreeNode } from '@/lib/queries';
import { landingFor } from '@/lib/roleHome';

/**
 * /onboarding — role-aware checklist. Each item has an `href` so the
 *   Start button actually navigates. Group admin sees the tenant-tree
 *   walk-through (Structure → Team → Partners → Settings); TPO sees
 *   the drive-lifecycle setup; student sees the profile → apply flow.
 *
 * Server-side progress derivation ships with #409. Until then the
 * checklist doesn't mark items done — the user just walks the path
 * with meaningful jumps.
 */

interface Item {
  title: string;
  body: string;
  href: string;
  cta?: string;
  /** Optional key the derived-state pass can flip to done. */
  derivedKey?: 'firstChild' | 'moreTeam' | 'firstPartner';
}

const CHECKLIST: Record<string, Item[]> = {
  student: [
    { title: 'Verify email', body: 'Click the link we sent to your inbox.', href: '/profile' },
    { title: 'Add a profile photo', body: 'Helps recruiters recognise you.', href: '/profile' },
    { title: 'List 5 skills', body: 'Nexie uses these to match you to drives.', href: '/skills' },
    { title: 'Upload a resume', body: 'Nexie extracts skills — you confirm before we save.', href: '/resume-builder' },
    { title: 'Preferences', body: 'Locations, roles, min CTC. All editable later.', href: '/profile' },
  ],
  tpo: [
    { title: 'Complete college profile', body: 'Address, principal, contact.', href: '/tpo/students' },
    { title: 'Import your student roster', body: "CSV or paste. We'll match Persona-2 signups.", href: '/tpo/students' },
    { title: 'Invite your team', body: 'Assistant TPO, coordinators, HoDs.', href: '/tpo/students' },
    { title: 'Add your first partner company', body: 'Or sign an MoU with a GLOBAL one.', href: '/tpo/partners' },
    { title: 'Post your first drive', body: 'Nexie fills the form from a JD in seconds.', href: '/tpo/drives/new' },
  ],
  group_admin: [
    { title: 'Add your first college', body: 'A group without colleges has nowhere to run drives. Open Structure to add one and invite the principal.', href: '/group/structure', cta: 'Open Structure', derivedKey: 'firstChild' },
    { title: 'Invite your group team', body: 'Group manager, accountant, or shared placement head — they see everything in the group workspace.', href: '/group/team', cta: 'Open Team' },
    { title: 'Add a partner company', body: 'Sign an MoU with a GLOBAL partner, or bring in your own private one.', href: '/group/partners', cta: 'Open Partners' },
    { title: 'Fill group settings', body: 'GST, PAN, invoice email — needed before your first invoice generates.', href: '/group/settings', cta: 'Open Settings' },
  ],
  university_admin: [
    { title: 'Register affiliated colleges', body: 'Constituent and affiliated colleges you host placement for. Each becomes its own child tenant.', href: '/group/structure', cta: 'Open Structure', derivedKey: 'firstChild' },
    { title: 'Invite university team', body: 'Registrar, exam controller, or dean of placement.', href: '/group/team', cta: 'Open Team' },
    { title: 'Add a partner company', body: 'Sign an MoU with a GLOBAL partner or bring in your own.', href: '/group/partners', cta: 'Open Partners' },
  ],
  principal: [
    { title: 'Add departments', body: 'CSE / ECE / MBA / Pharmacy — every department that runs placements needs a row.', href: '/tpo/students', cta: 'Manage People', derivedKey: 'firstChild' },
    { title: 'Invite TPO team + HoDs', body: 'Assistant TPO, coordinators, HoD per department.', href: '/tpo/students', cta: 'Invite Team' },
    { title: 'Import student roster', body: 'CSV upload or paste. Matches Persona-2 signups.', href: '/tpo/students', cta: 'Import' },
    { title: 'Add your first partner', body: 'GLOBAL partner via MoU, or a private recruiter.', href: '/tpo/partners', cta: 'Open Partners' },
  ],
  hod: [
    { title: 'Roster your students', body: 'Batch-year list of students you mentor.', href: '/hod/department', cta: 'Open Department' },
    { title: 'Invite mentor faculty', body: 'Assign faculty to student cohorts so mentee visits are tracked.', href: '/hod/department', cta: 'Invite Team' },
  ],
  default: [
    { title: 'Complete your profile', body: 'Basic identity so people know who you are.', href: '/profile' },
    { title: 'Enable notifications', body: 'Email + in-app first; WhatsApp when it goes live.', href: '/settings' },
  ],
};

function checklistKeyFor(role: string): keyof typeof CHECKLIST {
  if (role === 'tpo' || role === 'assistant_tpo') return 'tpo';
  if (role === 'student' || role === 'student_representative') return 'student';
  if (role === 'group_admin' || role === 'group_manager' || role === 'group_accountant') return 'group_admin';
  if (role === 'university_admin' || role === 'university_registrar' || role === 'university_exam_controller') return 'university_admin';
  if (role === 'principal' || role === 'vice_principal') return 'principal';
  if (role === 'hod' || role === 'placement_coordinator') return 'hod';
  return 'default';
}

export function OnboardingPage() {
  const { user, loading } = useAuthStore();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) nav('/login', { replace: true });
  }, [loading, user, nav]);

  // Fetch the org tree so we can mark "add your first child" as done
  // once a child exists. Only enabled for tenant-admin roles — students
  // and TPOs don't need it.
  const enableTree = user?.role
    ? ['group_admin', 'group_manager', 'university_admin', 'university_registrar', 'principal', 'vice_principal', 'hod', 'placement_coordinator'].includes(user.role)
    : false;
  const orgTree = useOrgTree();
  const derivedDone = useMemo(() => {
    if (!enableTree || !orgTree.data?.tree) return {};
    const root: OrgTreeNode = orgTree.data.tree;
    return { firstChild: root.children.length > 0 };
  }, [enableTree, orgTree.data]);

  if (!user) return null;

  const shellKey = checklistKeyFor(user.role);
  const items = CHECKLIST[shellKey] ?? CHECKLIST.default ?? [];
  const est = items.length <= 2 ? '2 min' : items.length <= 4 ? '5 min' : '10 min';
  const doneCount = items.filter((it) => it.derivedKey && (derivedDone as Record<string, boolean>)[it.derivedKey]).length;

  return (
    <RuledPage>
      <section className="max-w-page-wide mx-auto px-6 py-12">
        <p className="eyebrow">Welcome, {user.fullName.split(' ')[0]}</p>
        <h1 className="font-serif text-4xl mb-2">Let's get you set up.</h1>
        <p className="text-ink-secondary mb-8">
          A short checklist so you get to real work fast. You can skip anything and come back later.
        </p>

        <div className="grid gap-3 md:grid-cols-3 mb-8">
          <KpiCard label="Steps done" value={doneCount} meta={`of ${items.length}`} tone={doneCount > 0 ? 'success' : undefined} />
          <KpiCard label="Est. time" value={est} tone="info" />
          <KpiCard label="Skip for now?" value="Fine" meta="Nudge only" />
        </div>

        <div className="grid gap-3">
          {items.map((it, i) => {
            const done = it.derivedKey ? !!(derivedDone as Record<string, boolean>)[it.derivedKey] : false;
            return (
              <Card
                key={i}
                padding="md"
                className="flex items-start gap-4"
                data-testid={`onboarding-item-${i}`}
                data-done={done ? 'true' : 'false'}
                style={done ? { opacity: 0.7 } : undefined}
              >
                <div
                  className="w-6 h-6 rounded-full border border-line-tertiary flex items-center justify-center text-xs"
                  style={done ? { background: 'rgb(var(--ink-success))', color: 'white', borderColor: 'rgb(var(--ink-success))' } : { color: 'rgb(var(--ink-tertiary))' }}
                >
                  {done ? '✓' : i + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium">
                    {it.title}{done && <span className="ml-2 text-xs text-ink-tertiary">Done</span>}
                  </div>
                  <div className="text-sm text-ink-secondary">{it.body}</div>
                </div>
                <Link to={it.href} data-testid={`onboarding-start-${i}`}>
                  <Button variant="secondary" size="sm">{done ? 'Open' : (it.cta ?? 'Start')}</Button>
                </Link>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 flex justify-between text-sm">
          <Link to={landingFor(user.role)} className="link">Skip and go to my dashboard</Link>
          <span className="text-ink-tertiary">You can restart onboarding from Settings.</span>
        </div>
      </section>
    </RuledPage>
  );
}
